
import React, { useState, useEffect } from "react";
import { Movie } from "@/types/movie";
import CollapsibleSection from "./CollapsibleSection";
import MovieCard from "@/components/movie/MovieCard";
import { getGroupHeaderText } from "../utils/searchResultsUtils";

interface SearchResultsContainerProps {
  sections: Array<{
    type: string;
    results: Movie[];
  }>;
  query: string;
  onOpenMovieDetails: (tmdbId: number) => void;
  selectedResultIndex?: number;
  resultsRef?: React.RefObject<HTMLDivElement>;
  onClose?: () => void;
}

const SearchResultsContainer: React.FC<SearchResultsContainerProps> = ({
  sections,
  query,
  onOpenMovieDetails,
  selectedResultIndex = -1,
  resultsRef,
  onClose
}) => {
  const [openSections, setOpenSections] = useState<Record<string, boolean>>(() => {
    const initialState: Record<string, boolean> = {};
    sections.forEach(section => {
      initialState[section.type] = true;
    });
    return initialState;
  });

  // Reset all sections to expanded when new search results come in
  useEffect(() => {
    const newOpenSections: Record<string, boolean> = {};
    sections.forEach(section => {
      newOpenSections[section.type] = true;
    });
    setOpenSections(newOpenSections);
  }, [sections]);

  const toggleSection = (sectionType: string) => {
    setOpenSections(prev => ({
      ...prev,
      [sectionType]: !prev[sectionType]
    }));
  };

  const totalResults = sections.reduce((sum, section) => sum + section.results.length, 0);

  if (totalResults === 0) return null;

  return (
    <div 
      ref={resultsRef}
      className="absolute mt-2 w-full max-w-2xl left-0 bg-mono-eerieBlack border border-mono-slateGray/30 rounded-md z-50 max-h-96 overflow-y-auto"
      aria-label={`${totalResults} search results for "${query}"`}
    >
      <div className="divide-y divide-mono-slateGray/30">
        {sections.map(({ type, results }) => {
          const headerText = getGroupHeaderText(type, results);
          const isOpen = openSections[type] ?? true;

          return (
            <CollapsibleSection
              key={type}
              title={headerText}
              count={results.length}
              isOpen={isOpen}
              onToggle={() => toggleSection(type)}
              sectionId={type}
            >
              <div>
                {results.map((movie) => {
                  // Ensure movie has tmdbId before rendering
                  if (!movie.tmdbId) {
                    console.warn('Search result missing tmdbId, skipping:', movie);
                    return null;
                  }
                  
                  // FIXED: Generate stable key that excludes favorite/planToWatch status to prevent remounts
                  const dynamicKey = `search-${movie.tmdbId}-${query}`;
                  
                  return (
                    <MovieCard
                      key={dynamicKey}
                      movie={movie}
                      viewMode="list"
                      onOpenMovieDetails={onOpenMovieDetails}
                      isSearchResult={true}
                      cardKey={dynamicKey}
                    />
                  );
                })}
              </div>
            </CollapsibleSection>
          );
        })}
      </div>
    </div>
  );
};

export default SearchResultsContainer;
